// Las fotos y archivos de un chat (13-10, Pau: «como en WhatsApp o Telegram: ver todos los archivos y las fotos que se
// han mandado; poder ver si se le ha enviado alguna foto anteriormente»). En la ficha, una fila con las últimas y
// «Ver todo»; la vista entera, con filtro de quién lo mandó. Sale de toda la conversación (servidor), no de lo cargado.
import { ChevronLeft, FileText, ImageOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { formatDiaMes } from '../../../lib/time';
import { getActiveBaseUrl } from '../../../stores/servers.store';
import { oficinaApi } from '../api';
import type { ArchivoDTO } from '../compartido/vocabulario';
import { BotonIcono } from '../ui';
import { MiniaturaImagen } from '../chat/VisorImagen';
import { useVisorDocumento } from '../chat/VisorDocumento';

const url = (agentId: string, a: ArchivoDTO) => `${getActiveBaseUrl()}/api/agents/${encodeURIComponent(agentId)}/attachments/${encodeURIComponent(a.id)}/raw`;

/** Los archivos del chat; se recargan cuando cambia `version` (p. ej. llega un mensaje nuevo). */
export function useArchivos(agentId: string, version: string): ArchivoDTO[] | null {
  const [archivos, setArchivos] = useState<ArchivoDTO[] | null>(null);
  useEffect(() => {
    let vivo = true;
    oficinaApi
      .archivos(agentId)
      .then((r) => vivo && setArchivos(r.archivos))
      .catch(() => vivo && setArchivos([]));
    return () => {
      vivo = false;
    };
  }, [agentId, version]);
  return archivos;
}

/**
 * Una foto de la galería. Las que ya no se pueden abrir (hasta el 24-10 el panel borraba los adjuntos a los 30 días) se ven como
 * «ya no está disponible» en vez de una imagen rota.
 */
function Foto({ agentId, a }: { agentId: string; a: ArchivoDTO }) {
  const [rota, setRota] = useState(false);
  return (
    <div className="aspect-square overflow-hidden bg-oficina-elevada [&_img]:h-full [&_img]:max-h-none">
      {rota ? (
        <span title={a.nombre} className="flex h-full w-full flex-col items-center justify-center gap-1 p-1 text-center text-o-mini text-oficina-texto3">
          <ImageOff className="h-5 w-5" aria-hidden />
          {t('oficinaV2.galeria.noDisponible')}
        </span>
      ) : (
        <MiniaturaImagen src={url(agentId, a)} nombre={a.nombre} alFallar={() => setRota(true)} />
      )}
    </div>
  );
}

/** La fila de la ficha: cuántos hay, las últimas fotos y «Ver todo». */
export function FilaArchivos({ agentId, archivos, alVerTodo }: { agentId: string; archivos: ArchivoDTO[] | null; alVerTodo: () => void }) {
  if (!archivos?.length) return null;
  const fotos = archivos.filter((a) => a.imagen).slice(0, 4);
  return (
    <section data-testid="fila-archivos">
      <div className="mb-1.5 flex items-center justify-between">
        <h3 className="text-o-nota font-semibold text-oficina-texto3">{t('oficinaV2.galeria.titulo')}</h3>
        <button type="button" onClick={alVerTodo} className="text-o-sub font-medium text-oficina-enlace hover:underline">
          {t('oficinaV2.galeria.verTodo', { n: archivos.length })}
        </button>
      </div>
      {fotos.length ? (
        <div className="grid grid-cols-4 gap-1 overflow-hidden rounded-o-s">
          {fotos.map((a) => (
            <Foto key={a.id} agentId={agentId} a={a} />
          ))}
        </div>
      ) : (
        <p className="text-o-sub text-oficina-texto2">{t('oficinaV2.galeria.soloDocumentos', { n: archivos.length })}</p>
      )}
    </section>
  );
}

type Filtro = 'todos' | 'pau' | 'agente';

/** La vista entera (ocupa la ficha): filtro, rejilla de fotos y lista de documentos. */
export function Galeria({ agentId, archivos, nombreAgente, alVolver }: { agentId: string; archivos: ArchivoDTO[]; nombreAgente: string; alVolver: () => void }) {
  const abrirDocumento = useVisorDocumento((s) => s.abrir);
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const lista = archivos.filter((a) => filtro === 'todos' || a.quien === filtro);
  const fotos = lista.filter((a) => a.imagen);
  const docs = lista.filter((a) => !a.imagen);
  return (
    <section data-testid="galeria" className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-1 px-2 pt-3">
        <BotonIcono icono={ChevronLeft} etiqueta={t('oficinaV2.galeria.volver')} onClick={alVolver} />
        <h2 className="text-o-cuerpo font-semibold text-oficina-texto">{t('oficinaV2.galeria.titulo')}</h2>
      </header>
      <div role="group" aria-label={t('oficinaV2.galeria.filtro')} className="flex gap-1.5 px-5 py-3">
        {(['todos', 'pau', 'agente'] as const).map((f) => (
          <button
            key={f}
            type="button"
            aria-pressed={filtro === f}
            onClick={() => setFiltro(f)}
            className={cn(
              'min-h-9 rounded-full px-3 text-o-sub font-medium transition-colors',
              filtro === f ? 'bg-oficina-acento text-oficina-acento-tinta' : 'bg-oficina-elevada text-oficina-texto2 hover:text-oficina-texto',
            )}
          >
            {f === 'todos' ? t('oficinaV2.galeria.todos') : f === 'pau' ? t('oficinaV2.galeria.tuyos') : t('oficinaV2.galeria.suyos')}
          </button>
        ))}
      </div>
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 pb-6">
        {lista.length === 0 ? <p className="text-o-texto text-oficina-texto3">{t('oficinaV2.galeria.nada')}</p> : null}
        {fotos.length ? (
          <div>
            <h3 className="mb-1.5 text-o-nota font-semibold text-oficina-texto3">{t('oficinaV2.galeria.fotos', { n: fotos.length })}</h3>
            <div className="grid grid-cols-3 gap-1 overflow-hidden rounded-o-s">
              {fotos.map((a) => (
                <Foto key={a.id} agentId={agentId} a={a} />
              ))}
            </div>
          </div>
        ) : null}
        {docs.length ? (
          <div>
            <h3 className="mb-1.5 text-o-nota font-semibold text-oficina-texto3">{t('oficinaV2.galeria.documentos', { n: docs.length })}</h3>
            <ul className="space-y-0.5">
              {docs.map((a) => (
                <li key={a.id}>
                  <button type="button" onClick={() => abrirDocumento({ agentId, id: a.id, nombre: a.nombre, tipo: a.tipo })} className="flex w-full items-center gap-3 rounded-o-s px-1 py-1.5 text-left hover:bg-oficina-hover">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-o-s bg-oficina-elevada text-oficina-texto2">
                      <FileText className="h-5 w-5" aria-hidden />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-o-texto text-oficina-texto">{a.nombre}</span>
                      <span className="block text-o-nota text-oficina-texto3">
                        {a.quien === 'pau' ? t('oficinaV2.galeria.deTi') : nombreAgente} · {formatDiaMes(a.en)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}
